import React, { useCallback, useState } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext, useWatch } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { FlowToolFlowSelectionModal } from "src/agents/AgentVersionPage/AgentToolsEditor/FlowToolFlowSelectionModal";
import { useSideDrawerCoordinator } from "src/agents/base/SideDrawer";
import { AgentVersionConfigurationInput } from "src/agents/types";
import {
  BUILT_IN_TOOLS as BUILT_IN_TOOL_DEFINITIONS,
  type BuiltInTool,
} from "src/api/agents/management";
import { Flow, FlowVersion, Schema } from "src/api/flowTypes";
import { ToolCard } from "src/base-components/agents/ToolCard";
import { Button } from "src/base-components/Button";
import { Switch } from "src/base-components/Switch";
import type { ToolType } from "src/clients/agent-management";
import {
  AgentVersionConfigurationInputToolsInner,
  FlowToolConfigInputOutputTypeEnum,
  MetadataOverlay,
} from "src/clients/agent-management";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

type AgentToolsEditorProps = {
  immutable: boolean;
};

// Built-in tools render as fixed toggles, separate from the configurable
// flow-tool list. The set of built-ins, their names, and descriptions come
// from BUILT_IN_TOOL_DEFINITIONS (the canonical registry in the API layer); add
// a new built-in there. Here we only attach the editor-specific data-loc.
type BuiltInToolDescriptor = BuiltInTool & { dataLoc: string };

const BUILT_IN_TOOLS: readonly BuiltInToolDescriptor[] =
  BUILT_IN_TOOL_DEFINITIONS.map((tool) => ({
    ...tool,
    // "read_file" -> "read-file", "calculator" -> "calculator"
    dataLoc: tool.type.replace(/_/g, "-"),
  }));

// Disclosure shown in the Web search info tooltip: the tool forwards queries to
// a third-party search sub-processor.
export const WEB_SEARCH_TOOL_INFO = "Powered by a third-party search provider";

const BUILT_IN_TOOL_TYPES: readonly string[] = BUILT_IN_TOOLS.map(
  (t) => t.type,
);

/**
 * Build metadata_overlay from I/O schema, preserving existing descriptions
 * when properties still exist in the new schema.
 */
export const buildMetadataOverlay = (
  schema?: Schema,
  existingOverlay?: MetadataOverlay,
): MetadataOverlay => {
  const properties = schema?.properties ?? {};

  const overlayProperties: Record<string, { description?: string }> = {};

  for (const propName of Object.keys(properties)) {
    const existingDescription =
      existingOverlay?.properties?.[propName]?.description;
    const schemaDescription = properties[propName]?.description;
    overlayProperties[propName] = {
      description: existingDescription ?? schemaDescription ?? "",
    };
  }

  return { properties: overlayProperties };
};

/**
 * Build a new tool from a selected flow and version.
 */
export const buildNewTool = (
  flow: Flow,
  version: FlowVersion,
): AgentVersionConfigurationInputToolsInner => {
  const inputMetadataOverlay = buildMetadataOverlay(version.input_schema);
  const outputMetadataOverlay = buildMetadataOverlay(version.output_schema);

  return {
    id: uuidv4(),
    type: "flow",
    name: flow.name,
    description: flow.meta.description ?? "",
    flow_slug: flow.slug,
    flow_version_id: version.id,
    output_type:
      FlowToolConfigInputOutputTypeEnum.JSON as AgentVersionConfigurationInputToolsInner["output_type"],
    input_schema_metadata_overlay: inputMetadataOverlay,
    output_schema_metadata_overlay: outputMetadataOverlay,
    input_schema: {},
    output_schema: {},
  };
};

/**
 * Update an existing tool with a new flow/version selection.
 */
export const buildUpdatedTool = (
  existingTool: AgentVersionConfigurationInputToolsInner,
  flow: Flow,
  version: FlowVersion,
): AgentVersionConfigurationInputToolsInner => {
  const inputMetadataOverlay = buildMetadataOverlay(
    version.input_schema,
    existingTool.input_schema_metadata_overlay,
  );
  const outputMetadataOverlay = buildMetadataOverlay(
    version.output_schema,
    existingTool.output_schema_metadata_overlay,
  );

  return {
    ...existingTool,
    flow_slug: flow.slug,
    flow_version_id: version.id,
    input_schema_metadata_overlay: inputMetadataOverlay,
    output_schema_metadata_overlay: outputMetadataOverlay,
  };
};

export const AgentToolsEditor: React.FC<AgentToolsEditorProps> = ({
  immutable,
}) => {
  const { setValue, getValues, control } =
    useFormContext<AgentVersionConfigurationInput>();
  const tools = useWatch({ name: "tools", control });
  // Drawer coordinator
  const { requestOpen, requestClose, openDrawer } = useSideDrawerCoordinator();

  // Configurable tools shown in the main list; built-ins are rendered separately with toggles
  const configurableTools = tools?.filter(
    (t) => !BUILT_IN_TOOL_TYPES.includes(t.type ?? ""),
  );

  // Flow selection modal state - uses id for existing tools
  const [flowModalState, setFlowModalState] = useState<
    { type: "new" } | { type: "existing"; toolId: string } | null
  >(null);

  // Open the config drawer for a flow tool by its id
  const openToolConfigDrawer = useCallback(
    (toolId: string) => {
      requestOpen({
        type: "flowToolConfig",
        data: {
          toolId,
          onRequestChangeFlow: () =>
            setFlowModalState({ type: "existing", toolId }),
        },
      });
    },
    [requestOpen],
  );

  // Handle flow/version selection from modal
  const handleFlowVersionSelected = useCallback(
    (flow: Flow, version: FlowVersion) => {
      // Get fresh tools value to avoid stale closure
      const currentTools = getValues("tools") ?? [];

      if (flowModalState?.type === "new") {
        const newTool = buildNewTool(flow, version);

        setValue("tools", [...currentTools, newTool]);
        openToolConfigDrawer(newTool.id);
      } else if (flowModalState?.type === "existing") {
        const { toolId } = flowModalState;
        const tool = currentTools.find((f) => f.id === toolId);
        if (tool) {
          const updatedTool = buildUpdatedTool(tool, flow, version);
          // Replace entire array to ensure re-render
          setValue(
            "tools",
            currentTools.map((t) => (t.id === toolId ? updatedTool : t)),
          );
          openToolConfigDrawer(toolId);
        }
      }
      setFlowModalState(null);
    },
    [flowModalState, setValue, getValues, openToolConfigDrawer],
  );

  // Handle toggling any built-in tool on/off. Built-ins are at most one per
  // agent version (backend enforces); duplicates are guarded against here too.
  const handleToggleBuiltInTool = useCallback(
    (tool: BuiltInToolDescriptor, enabled: boolean) => {
      const currentTools = getValues("tools") ?? [];
      if (enabled) {
        const alreadyExists = currentTools.some((t) => t.type === tool.type);
        if (alreadyExists) return;
        setValue("tools", [...currentTools, tool.build()]);
      } else {
        setValue(
          "tools",
          currentTools.filter((t) => t.type !== tool.type),
        );
      }
    },
    [getValues, setValue],
  );

  // Handle deleting a tool
  const handleDeleteTool = useCallback(
    (toolId: string) => {
      if (
        openDrawer?.type === "flowToolConfig" &&
        openDrawer.data?.toolId === toolId
      ) {
        requestClose();
      }
      const currentTools = getValues("tools") ?? [];
      setValue(
        "tools",
        currentTools.filter((t) => t.id !== toolId),
      );
    },
    [requestClose, setValue, getValues, openDrawer],
  );

  // Handle clicking "+ Add tool" button
  const handleAddTool = useCallback(() => {
    requestOpen({
      type: "addTool",
      data: {
        onAddFlowTool: () => setFlowModalState({ type: "new" }),
      },
    });
  }, [requestOpen]);

  // Handle flow selection modal close
  const handleFlowModalClose = useCallback(() => {
    setFlowModalState(null);
  }, []);

  return (
    <>
      <div>
        <div className="rounded-lg bg-gray-50 p-5 pr-8">
          {!immutable && (
            <div className="absolute top-3 right-6">
              <Button
                dataLoc="add-tool-button"
                size="sm"
                variant="secondary"
                onClick={handleAddTool}
              >
                + Add tool
              </Button>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {BUILT_IN_TOOLS.filter(
              // Web search is gated behind a feature flag for demos/early access.
              (tool) =>
                tool.type !== "web_search" ||
                isFeatureFlagEnabled(FEATURE_FLAGS.agentWebSearchTool),
            ).map((tool) => {
              const enabled = (tools ?? []).some((t) => t.type === tool.type);
              return (
                <ToolCard
                  key={tool.type}
                  dataLoc={`tool-card-${tool.dataLoc}`}
                  name={tool.name}
                  description={tool.description}
                  titleAdornment={
                    tool.type === "web_search" ? (
                      <Tooltip
                        body={WEB_SEARCH_TOOL_INFO}
                        dataLoc="web-search-info-tooltip"
                        placement="top"
                      >
                        <span className="inline-flex text-gray-500">
                          <Icon icon={faCircleInfo} size="sm" />
                        </span>
                      </Tooltip>
                    ) : undefined
                  }
                  type={tool.type}
                  rightSlot={
                    <div className="flex-shrink-0">
                      <Switch
                        dataLoc={`${tool.dataLoc}-tool-toggle`}
                        disabled={immutable}
                        enabled={enabled}
                        onChange={(value) =>
                          handleToggleBuiltInTool(tool, value)
                        }
                      />
                    </div>
                  }
                />
              );
            })}
            {configurableTools?.map((tool) => {
              const isSelected =
                openDrawer?.type === "flowToolConfig" &&
                openDrawer.data?.toolId === tool.id;
              return (
                <ToolCard
                  key={tool.id}
                  dataLoc={`tool-card-${tool.id}`}
                  description={tool.description}
                  name={tool.name}
                  selected={isSelected}
                  type={tool.type as ToolType}
                  onClick={() => openToolConfigDrawer(tool.id)}
                  onDelete={
                    immutable ? undefined : () => handleDeleteTool(tool.id)
                  }
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Flow selection modal - rendered here so AgentToolsEditor owns its state */}
      <FlowToolFlowSelectionModal
        open={flowModalState !== null}
        onClose={handleFlowModalClose}
        onSelect={handleFlowVersionSelected}
      />
    </>
  );
};
